import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Button, Checkbox, Modal } from '@/components/ui'
import { Vouchers } from '../../../common/models'
import { formatDateTime } from '../../../core/constants/times'
import { transferTicketVouchers } from '../../../core/services/voucher'
import { MessageBox } from '../messages/MessageBox'

interface IValues {
  vouchers: string[]
}

export interface TransferVouchersProps {
  visible: boolean
  setVisible: (visible: boolean) => void
  vouchers: Vouchers[]
  fromTicketId: string
  toTicketId: string
}

const VoucherItem = ({
  item,
  values,
  onChange,
}: {
  item: Vouchers
  values: string[]
  onChange: (id: string) => void
}) => {
  return (
    <div className="mb-2 flex items-center justify-center gap-4">
      <Checkbox
        checked={values.includes(item.id)}
        onCheckedChange={() => {
          onChange(item.id)
        }}
      />
      <div className="mx-4 flex flex-col">
        <span>Voucher Name</span>
        <span className="text-center font-medium text-accent-1">
          {item.label || '-'}
        </span>
      </div>
      <div className="mx-4 flex flex-col">
        <span>Expiration Date</span>
        <span className="text-center font-medium text-accent-1">
          {formatDateTime(item.publishDate)}
        </span>
      </div>
      <div className="mx-4 flex flex-col">
        <span>Discount</span>
        <span className="text-center font-medium text-accent-1">
          {item.discountType === 'AMOUNT'
            ? `RON${item.amount}`
            : `${item.amount}%`}
        </span>
      </div>
    </div>
  )
}

export const TransferVouchers = (props: TransferVouchersProps): JSX.Element => {
  const { visible, setVisible, vouchers, fromTicketId, toTicketId } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const handleClose = () => {
    setVisible(false)
  }

  const schema = yup.object().shape({
    vouchers: yup.array().of(yup.string().required()).min(0).required(),
  })

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(schema),
    defaultValues: {
      vouchers: [],
    },
  })

  const submit = async (values: IValues) => {
    try {
      setSubmitting(true)
      const { status } = await transferTicketVouchers({
        toTicketId,
        fromTicketId,
        voucherIds: values.vouchers,
      })
      if (status === 200) {
        setMessage('Vouchers transferred successfully')
        setIsError(false)
        setShowMessageBox(true)
        setVisible(false)
      }
    } catch (err) {
      if (err instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(err.message)
      } else {
        console.error(err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title="Transfer Vouchers"
        description="You have available vouchers for previous ticket, do you want to transfer them?"
        footer={
          <div className="flex w-full items-center gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="tickets-addon-form"
              type="submit"
              variant="primary"
              disabled={!isValid || submitting}
              loading={submitting}
            >
              Transfer Now
            </Button>
          </div>
        }
      >
        <form
          id="tickets-addon-form"
          className="mt-4 px-4"
          onSubmit={handleSubmit(submit)}
        >
          <Controller
            name="vouchers"
            control={control}
            render={({ field }) => (
              <div>
                {vouchers.map((voucher, index) => (
                  <VoucherItem
                    values={field.value}
                    item={voucher}
                    key={index}
                    onChange={(id) => {
                      const values = field.value
                      if (values.includes(id)) {
                        const index = values.indexOf(id)
                        values.splice(index, 1)
                      } else {
                        values.push(id)
                      }
                      field.onChange(values)
                    }}
                  />
                ))}
              </div>
            )}
          />
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
